import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Wallet, Play, Eye, Edit, Trash2, Printer, Search, RefreshCw,
  LayoutGrid, List as ListIcon, History, ChevronRight, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { openPrintWindow } from "@/utils/printHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLUMNS = [
  { key: "Draft",       label: "Draft",       chip: "bg-gray-700 text-gray-300",                bar: "border-gray-600",       accent: "from-gray-500/10" },
  { key: "Not Paid",    label: "Not Paid",    chip: "bg-red-900/40 text-red-300 border border-red-500/30",      bar: "border-red-500/40",     accent: "from-red-500/10" },
  { key: "In Process",  label: "In Process",  chip: "bg-blue-900/40 text-blue-300 border border-blue-500/30",   bar: "border-blue-500/40",    accent: "from-blue-500/10" },
  { key: "On Hold",     label: "Hold",        chip: "bg-amber-900/40 text-amber-300 border border-amber-500/30", bar: "border-amber-500/40",  accent: "from-amber-500/10" },
  { key: "Salary Paid", label: "Salary Paid", chip: "bg-green-900/40 text-green-300 border border-green-500/30", bar: "border-green-500/40",  accent: "from-green-500/10" },
  { key: "Rejected",    label: "Rejected",    chip: "bg-rose-900/40 text-rose-300 border border-rose-500/30",   bar: "border-rose-500/40",    accent: "from-rose-500/10" },
];

const statusMeta = (s) =>
  STATUS_COLUMNS.find((c) => c.key === s) || STATUS_COLUMNS[0];

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const HRPayroll = () => {
  const [records, setRecords] = useState([]);
  const [selMonth, setSelMonth] = useState(new Date().getMonth() + 1);
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const [showPayslip, setShowPayslip] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [editForm, setEditForm] = useState({ bonus: "0", incentives: "0", advances: "0" });
  const [search, setSearch] = useState("");
  const [view, setView] = useState("kanban"); // 'kanban' | 'table'

  // Status change dialog state
  const [statusFor, setStatusFor] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "Not Paid", reason: "" });
  const [showHistory, setShowHistory] = useState(null);

  const fetchData = useCallback(async () => {
    const res = await axios.get(`${API}/hr/payroll`, { params: { month: selMonth, year: selYear } });
    setRecords(res.data);
  }, [selMonth, selYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async (regenerate = false) => {
    if (regenerate && !window.confirm(
      "Recompute draft payroll for this month from the latest attendance?\n\n" +
      "• Draft rows will be replaced with fresh values.\n" +
      "• Locked / Paid / Released rows are NEVER touched.\n" +
      "• Advance deductions on the old draft will be reversed before recompute (clean ledger)."
    )) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/hr/payroll/generate`, { month: selMonth, year: selYear, regenerate });
      const parts = [];
      if (res.data.generated) parts.push(`${res.data.generated} new`);
      if (res.data.regenerated) parts.push(`${res.data.regenerated} re-computed`);
      if (res.data.skipped_locked) parts.push(`${res.data.skipped_locked} skipped (locked)`);
      toast.success(parts.length ? `Payroll: ${parts.join(' · ')}` : "Nothing to update");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setGenerating(false); }
  };

  const handleUpdatePayroll = async () => {
    try {
      await axios.put(`${API}/hr/payroll/${showEdit.id}`, editForm);
      toast.success("Updated");
      setShowEdit(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payroll entry?")) return;
    await axios.delete(`${API}/hr/payroll/${id}`);
    toast.success("Deleted");
    fetchData();
  };

  const viewPayslip = async (rec) => {
    try {
      const res = await axios.get(`${API}/hr/payroll/${rec.id}/payslip`);
      setShowPayslip(res.data);
    } catch { toast.error("Failed to load payslip"); }
  };

  const openStatusDialog = (rec) => {
    setStatusFor(rec);
    setStatusForm({ status: rec.status || "Not Paid", reason: "" });
  };

  const handleStatusChange = async () => {
    if (!statusFor) return;
    if (!statusForm.reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await axios.patch(`${API}/hr/payroll/${statusFor.id}/status`, {
        status: statusForm.status,
        reason: statusForm.reason.trim(),
        changed_by: localStorage.getItem("username") || "user",
      });
      toast.success(`Marked as ${statusForm.status}`);
      setStatusFor(null);
      setStatusForm({ status: "Not Paid", reason: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change status");
    }
  };

  const printPayslip = () => {
    if (!showPayslip) return;
    const p = showPayslip.payroll;
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const html = `<!DOCTYPE html><html><head><title>Payslip - ${p.employee_name}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px;max-width:800px;margin:auto}
    table{width:100%;border-collapse:collapse;margin:10px 0}
    td,th{border:1px solid #333;padding:8px;text-align:left}
    th{background:#f0f0f0}.right{text-align:right}.header{text-align:center;margin-bottom:20px}
    .total{font-weight:bold;background:#e8f5e9}h2{margin:0}
    @media print{body{padding:0}}</style></head><body>
    <div class="header"><h2>CHALLENGE PACK INDUSTRIES</h2><p>Pay Slip for ${monthNames[p.month - 1]} ${p.year}</p></div>
    <table><tr><td><strong>Employee:</strong> ${p.employee_name}</td><td><strong>Type:</strong> ${p.salary_type === "fixed" ? "Fixed" : "Daily Wage"}</td></tr>
    <tr><td><strong>Working Days:</strong> ${p.working_days}</td><td><strong>Days Present:</strong> ${p.effective_days}</td></tr>
    <tr><td><strong>Days Absent:</strong> ${p.days_absent}</td><td><strong>Overtime Hours:</strong> ${p.overtime_hours || 0}</td></tr></table>
    <table><tr><th>Earnings</th><th class="right">Amount</th><th>Deductions</th><th class="right">Amount</th></tr>
    <tr><td>Basic Pay</td><td class="right">${p.basic_earned.toFixed(2)}</td><td>PF</td><td class="right">${p.pf.toFixed(2)}</td></tr>
    <tr><td>DA</td><td class="right">${(p.da || 0).toFixed(2)}</td><td>ESI</td><td class="right">${p.esi.toFixed(2)}</td></tr>
    <tr><td>HRA</td><td class="right">${(p.hra || 0).toFixed(2)}</td><td>Professional Tax</td><td class="right">${p.pt.toFixed(2)}</td></tr>
    <tr><td>Conveyance</td><td class="right">${(p.conveyance || 0).toFixed(2)}</td><td>TDS</td><td class="right">${(p.tds || 0).toFixed(2)}</td></tr>
    <tr><td>Other Allowances</td><td class="right">${(p.other_allowances || 0).toFixed(2)}</td><td>Advances</td><td class="right">${(p.advances || 0).toFixed(2)}</td></tr>
    <tr><td>Overtime Pay</td><td class="right">${(p.overtime_pay || 0).toFixed(2)}</td><td></td><td></td></tr>
    <tr><td>Bonus</td><td class="right">${(p.bonus || 0).toFixed(2)}</td><td></td><td></td></tr>
    <tr><td>Incentives</td><td class="right">${(p.incentives || 0).toFixed(2)}</td><td></td><td></td></tr>
    <tr class="total"><td>Total Earnings</td><td class="right">${p.total_earnings.toFixed(2)}</td><td>Total Deductions</td><td class="right">${p.total_deductions.toFixed(2)}</td></tr></table>
    <table><tr class="total" style="background:#c8e6c9;font-size:18px"><td>NET PAY</td><td class="right" style="font-size:18px">${formatINR(p.net_pay)}</td></tr></table>
    <p style="margin-top:40px;text-align:right"><strong>Authorized Signatory</strong></p>
    </body></html>`;
    openPrintWindow(html);
  };

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const filtered = useMemo(
    () => records.filter(r => !search || r.employee_name.toLowerCase().includes(search.toLowerCase())),
    [records, search]
  );
  const totalNet = filtered.reduce((s, r) => s + (r.net_pay || 0), 0);

  // Group by status for Kanban
  const grouped = useMemo(() => {
    const map = Object.fromEntries(STATUS_COLUMNS.map((c) => [c.key, []]));
    filtered.forEach((r) => {
      const k = STATUS_COLUMNS.some(c => c.key === r.status) ? r.status : "Draft";
      map[k].push(r);
    });
    return map;
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="hr-payroll-page">
      {/* Header */}
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-teal-400 flex items-center gap-2">
            <Wallet className="w-5 h-5" /> Payroll
          </h2>
          <p className="text-gray-400 text-sm">Monthly salary processing & payment tracking</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex border border-gray-700 rounded-md overflow-hidden" data-testid="payroll-view-toggle">
            <Button
              size="sm" variant="ghost"
              onClick={() => setView("kanban")}
              className={`rounded-none h-8 px-3 ${view === "kanban" ? "bg-teal-600 text-white hover:bg-teal-600" : "text-gray-400"}`}
              data-testid="view-kanban-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1" /> Kanban
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => setView("table")}
              className={`rounded-none h-8 px-3 ${view === "table" ? "bg-teal-600 text-white hover:bg-teal-600" : "text-gray-400"}`}
              data-testid="view-table-btn"
            >
              <ListIcon className="w-4 h-4 mr-1" /> Table
            </Button>
          </div>

          <Button size="sm" variant="outline" onClick={() => handleGenerate(true)} disabled={generating}
            className="border-amber-600 text-amber-300 hover:bg-amber-900/30" data-testid="regenerate-payroll-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${generating ? "animate-spin" : ""}`} />
            Recompute
          </Button>
          <Button size="sm" onClick={() => handleGenerate(false)} disabled={generating}
            className="bg-teal-600 hover:bg-teal-700" data-testid="generate-payroll-btn">
            <Play className="w-4 h-4 mr-1" /> {generating ? "Generating..." : "Generate"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <Select value={String(selMonth)} onValueChange={v => setSelMonth(parseInt(v))}>
          <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-32"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)} className="text-white">{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(selYear)} onValueChange={v => setSelYear(parseInt(v))}>
          <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-24"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9 w-48" />
        </div>
        {filtered.length > 0 && (
          <span className="text-emerald-400 font-mono font-bold ml-auto" data-testid="payroll-total">
            Total Net: {formatINR(totalNet)}
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card className="bg-gray-900/30 border-gray-700">
          <CardContent className="p-12 text-center">
            <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No payroll records for this period.</p>
            <p className="text-gray-500 text-sm mt-1">Click "Generate" to create from attendance.</p>
          </CardContent>
        </Card>
      ) : view === "kanban" ? (
        <KanbanView
          grouped={grouped}
          onStatusClick={openStatusDialog}
          onViewPayslip={viewPayslip}
          onEdit={(r) => { setShowEdit(r); setEditForm({ bonus: String(r.bonus || 0), incentives: String(r.incentives || 0), advances: String(r.advances || 0) }); }}
          onDelete={handleDelete}
          onShowHistory={setShowHistory}
        />
      ) : (
        <TableView
          records={filtered}
          onStatusClick={openStatusDialog}
          onViewPayslip={viewPayslip}
          onEdit={(r) => { setShowEdit(r); setEditForm({ bonus: String(r.bonus || 0), incentives: String(r.incentives || 0), advances: String(r.advances || 0) }); }}
          onDelete={handleDelete}
          onShowHistory={setShowHistory}
        />
      )}

      {/* Status Change Dialog (required reason) */}
      {statusFor && (
        <Dialog open={!!statusFor} onOpenChange={() => setStatusFor(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="status-change-dialog">
            <DialogHeader>
              <DialogTitle className="text-teal-400">
                Change Status — {statusFor.employee_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-xs text-gray-500">
                Current: <span className={`px-2 py-0.5 rounded ${statusMeta(statusFor.status).chip}`}>{statusFor.status || "Draft"}</span>
              </div>
              <div>
                <Label className="text-gray-300">New Status *</Label>
                <Select value={statusForm.status} onValueChange={v => setStatusForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {STATUS_COLUMNS.map(c => (
                      <SelectItem key={c.key} value={c.key} className="text-white">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Reason / Description *</Label>
                <Textarea
                  value={statusForm.reason}
                  onChange={e => setStatusForm(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Why this status? (required — saved to history)"
                  className="bg-gray-800 border-gray-600 text-white mt-1 min-h-[80px]"
                  data-testid="status-reason-input"
                />
                <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Every status change is logged with who/when/reason.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusFor(null)} className="border-gray-600 text-gray-300">
                Cancel
              </Button>
              <Button onClick={handleStatusChange} className="bg-teal-600 hover:bg-teal-700" data-testid="status-save-btn">
                Save Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Status History Dialog */}
      {showHistory && (
        <Dialog open={!!showHistory} onOpenChange={() => setShowHistory(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-teal-400 flex items-center gap-2">
                <History className="w-5 h-5" />
                Status History — {showHistory.employee_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {(showHistory.status_history || []).length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-6">No status changes recorded yet.</p>
              ) : (
                (showHistory.status_history || []).slice().reverse().map((h) => (
                  <div key={h.id} className="bg-gray-800/60 border border-gray-700 rounded-md p-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${statusMeta(h.from_status).chip}`}>{h.from_status}</span>
                      <ChevronRight className="w-3 h-3 text-gray-500" />
                      <span className={`px-2 py-0.5 rounded ${statusMeta(h.to_status).chip}`}>{h.to_status}</span>
                      <span className="text-gray-500 ml-auto">
                        {new Date(h.changed_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-200 mt-2">{h.reason}</p>
                    <p className="text-[11px] text-gray-500 mt-1">by {h.changed_by}</p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Payslip View */}
      {showPayslip && (
        <Dialog open={!!showPayslip} onOpenChange={() => setShowPayslip(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-teal-400 flex items-center justify-between">
                <span>Pay Slip — {showPayslip.payroll.employee_name}</span>
                <Button size="sm" variant="outline" onClick={printPayslip} className="border-teal-600 text-teal-300"><Printer className="w-4 h-4 mr-1" /> Print</Button>
              </DialogTitle>
            </DialogHeader>
            {(() => {
              const p = showPayslip.payroll;
              const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
              return (
                <div className="space-y-4 text-sm">
                  <div className="text-center bg-teal-950/30 border border-teal-500/30 rounded-lg p-3">
                    <div className="text-lg font-bold text-teal-400">CHALLENGE PACK INDUSTRIES</div>
                    <div className="text-gray-400">Pay Slip for {monthNames[p.month - 1]} {p.year}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 bg-gray-800/50 p-3 rounded-lg">
                    <div><span className="text-gray-400">Employee:</span> <span className="text-white font-medium ml-1">{p.employee_name}</span></div>
                    <div><span className="text-gray-400">Type:</span> <span className="text-white ml-1">{p.salary_type === "regular" ? "Regular" : p.salary_type === "contract" ? "Contract" : "Daily"}</span></div>
                    <div><span className="text-gray-400">Working Days:</span> <span className="text-white ml-1">{p.working_days}</span></div>
                    <div><span className="text-gray-400">Effective Days:</span> <span className="text-white ml-1">{p.effective_days}</span></div>
                    <div><span className="text-gray-400">Days Absent:</span> <span className="text-red-400 ml-1">{p.days_absent}</span></div>
                    <div><span className="text-gray-400">Overtime:</span> <span className="text-cyan-400 ml-1">{p.overtime_hours || 0} hrs</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="text-green-400 font-bold border-b border-gray-700 pb-1">Earnings</div>
                      <div className="flex justify-between"><span className="text-gray-400">Basic Pay</span><span className="text-white font-mono">{p.basic_earned.toFixed(2)}</span></div>
                      {p.da > 0 && <div className="flex justify-between"><span className="text-gray-400">DA</span><span className="text-white font-mono">{p.da.toFixed(2)}</span></div>}
                      {p.hra > 0 && <div className="flex justify-between"><span className="text-gray-400">HRA</span><span className="text-white font-mono">{p.hra.toFixed(2)}</span></div>}
                      {p.conveyance > 0 && <div className="flex justify-between"><span className="text-gray-400">Conveyance</span><span className="text-white font-mono">{p.conveyance.toFixed(2)}</span></div>}
                      {p.other_allowances > 0 && <div className="flex justify-between"><span className="text-gray-400">Other</span><span className="text-white font-mono">{p.other_allowances.toFixed(2)}</span></div>}
                      {p.overtime_pay > 0 && <div className="flex justify-between"><span className="text-gray-400">Overtime Pay</span><span className="text-cyan-400 font-mono">{p.overtime_pay.toFixed(2)}</span></div>}
                      {p.bonus > 0 && <div className="flex justify-between"><span className="text-gray-400">Bonus</span><span className="text-white font-mono">{p.bonus.toFixed(2)}</span></div>}
                      {p.incentives > 0 && <div className="flex justify-between"><span className="text-gray-400">Incentives</span><span className="text-white font-mono">{p.incentives.toFixed(2)}</span></div>}
                      <div className="flex justify-between border-t border-gray-700 pt-1 font-bold"><span className="text-green-400">Total Earnings</span><span className="text-green-400 font-mono">{p.total_earnings.toFixed(2)}</span></div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-red-400 font-bold border-b border-gray-700 pb-1">Deductions</div>
                      <div className="flex justify-between"><span className="text-gray-400">PF</span><span className="text-white font-mono">{p.pf.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">ESI</span><span className="text-white font-mono">{p.esi.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">PT</span><span className="text-white font-mono">{p.pt.toFixed(2)}</span></div>
                      {p.tds > 0 && <div className="flex justify-between"><span className="text-gray-400">TDS</span><span className="text-white font-mono">{p.tds.toFixed(2)}</span></div>}
                      {p.advances > 0 && <div className="flex justify-between"><span className="text-gray-400">Advances</span><span className="text-white font-mono">{p.advances.toFixed(2)}</span></div>}
                      <div className="flex justify-between border-t border-gray-700 pt-1 font-bold"><span className="text-red-400">Total Deductions</span><span className="text-red-400 font-mono">{p.total_deductions.toFixed(2)}</span></div>
                    </div>
                  </div>
                  <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-lg p-4 text-center">
                    <div className="text-gray-400 text-xs uppercase">Net Pay</div>
                    <div className="text-3xl font-bold text-emerald-400 font-mono">{formatINR(p.net_pay)}</div>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Bonus/Advances Dialog */}
      {showEdit && (
        <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white">
            <DialogHeader><DialogTitle className="text-teal-400">Edit Amounts — {showEdit.employee_name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-amber-300 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Status changes happen in the separate "Change Status" dialog (status pill).
              </p>
              <div><Label className="text-gray-300">Bonus</Label><Input type="number" value={editForm.bonus} onChange={e => setEditForm({...editForm, bonus: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Incentives</Label><Input type="number" value={editForm.incentives} onChange={e => setEditForm({...editForm, incentives: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Advances / Loan Deduction</Label><Input type="number" value={editForm.advances} onChange={e => setEditForm({...editForm, advances: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(null)} className="border-gray-600 text-gray-300">Cancel</Button>
              <Button onClick={handleUpdatePayroll} className="bg-teal-600 hover:bg-teal-700">Update & Recalculate</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


/* ───────────── Kanban view ───────────── */
const KanbanView = ({ grouped, onStatusClick, onViewPayslip, onEdit, onDelete, onShowHistory }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="payroll-kanban">
    {STATUS_COLUMNS.map(col => {
      const rows = grouped[col.key] || [];
      const colTotal = rows.reduce((s, r) => s + (r.net_pay || 0), 0);
      return (
        <div key={col.key} className={`bg-gradient-to-b ${col.accent} to-transparent border ${col.bar} rounded-lg flex flex-col min-h-[140px]`}>
          <div className="px-3 py-2 border-b border-gray-700/60 flex items-center justify-between">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${col.chip}`}>{col.label}</span>
            <span className="text-xs text-gray-400">{rows.length}</span>
          </div>
          <div className="p-2 space-y-2 flex-1">
            {rows.map(r => (
              <PayrollCard
                key={r.id} rec={r}
                onStatusClick={() => onStatusClick(r)}
                onViewPayslip={() => onViewPayslip(r)}
                onEdit={() => onEdit(r)}
                onDelete={() => onDelete(r.id)}
                onShowHistory={() => onShowHistory(r)}
              />
            ))}
            {rows.length === 0 && (
              <p className="text-[11px] text-gray-600 text-center py-4">No entries</p>
            )}
          </div>
          {colTotal > 0 && (
            <div className="px-3 py-1 border-t border-gray-700/60 text-[11px] text-gray-400 text-right">
              <span className="text-emerald-300 font-mono">{formatINR(colTotal)}</span>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const PayrollCard = ({ rec, onStatusClick, onViewPayslip, onEdit, onDelete, onShowHistory }) => {
  const hist = rec.status_history || [];
  return (
    <div className="bg-gray-900/70 border border-gray-700 rounded-md p-2.5 hover:border-teal-500/50 transition-colors" data-testid={`payroll-card-${rec.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-white truncate flex-1">{rec.employee_name}</div>
        <span className="text-[10px] px-1 py-0.5 rounded bg-gray-800 text-gray-400">{rec.salary_type === "regular" ? "Reg" : rec.salary_type === "contract" ? "Cont" : "Daily"}</span>
      </div>
      <div className="mt-1 text-xs text-gray-400 flex items-center justify-between">
        <span>Days: {rec.effective_days}/{rec.working_days}</span>
        <span className="text-emerald-400 font-mono font-bold">{formatINR(rec.net_pay)}</span>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-teal-600/50 text-teal-300 hover:bg-teal-900/30" onClick={onStatusClick} data-testid={`change-status-btn-${rec.id}`}>
          Change Status
        </Button>
        <div className="flex">
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-white" onClick={onViewPayslip}><Eye className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-white" onClick={onEdit}><Edit className="w-3.5 h-3.5" /></Button>
          {hist.length > 0 && (
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-blue-300" onClick={onShowHistory} title={`${hist.length} status change${hist.length > 1 ? "s" : ""}`}><History className="w-3.5 h-3.5" /></Button>
          )}
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-300" onClick={onDelete}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {rec.status_reason && (
        <p className="mt-1.5 text-[10px] text-gray-500 italic truncate" title={rec.status_reason}>
          “{rec.status_reason}”
        </p>
      )}
    </div>
  );
};


/* ───────────── Table view ───────────── */
const TableView = ({ records, onStatusClick, onViewPayslip, onEdit, onDelete, onShowHistory }) => (
  <Card className="bg-gray-900/30 border-gray-700" data-testid="payroll-table">
    <CardContent className="p-0 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-gray-700">
            <TableHead className="text-gray-400">Employee</TableHead>
            <TableHead className="text-gray-400">Type</TableHead>
            <TableHead className="text-gray-400 text-right">Days</TableHead>
            <TableHead className="text-gray-400 text-right">Gross</TableHead>
            <TableHead className="text-gray-400 text-right">Deductions</TableHead>
            <TableHead className="text-gray-400 text-right">Net Pay</TableHead>
            <TableHead className="text-gray-400">Status</TableHead>
            <TableHead className="text-gray-400 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map(r => {
            const meta = statusMeta(r.status);
            const hist = r.status_history || [];
            return (
              <TableRow key={r.id} className="border-gray-800">
                <TableCell className="text-white font-medium">
                  {r.employee_name}
                  {r.status_reason && (
                    <p className="text-[10px] text-gray-500 italic mt-0.5">“{r.status_reason}”</p>
                  )}
                </TableCell>
                <TableCell><span className={`px-2 py-1 rounded text-xs ${r.salary_type === "regular" ? "bg-blue-900/40 text-blue-400" : r.salary_type === "contract" ? "bg-purple-900/40 text-purple-400" : "bg-amber-900/40 text-amber-400"}`}>{r.salary_type === "regular" ? "Regular" : r.salary_type === "contract" ? "Contract" : "Daily"}</span></TableCell>
                <TableCell className="text-right text-gray-300">{r.effective_days}/{r.working_days}</TableCell>
                <TableCell className="text-right text-gray-300 font-mono">{r.total_earnings.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right text-red-400 font-mono">{r.total_deductions.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right text-emerald-400 font-bold font-mono">{formatINR(r.net_pay)}</TableCell>
                <TableCell>
                  <button onClick={() => onStatusClick(r)} className={`px-2 py-1 rounded text-xs font-medium ${meta.chip} hover:opacity-80`} data-testid={`status-pill-${r.id}`}>
                    {r.status || "Draft"}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" className="text-teal-400 hover:text-teal-300 h-8 px-2" onClick={() => onViewPayslip(r)}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => onEdit(r)}><Edit className="w-4 h-4" /></Button>
                  {hist.length > 0 && (
                    <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-8 px-2" onClick={() => onShowHistory(r)} title={`${hist.length} change${hist.length > 1 ? "s" : ""}`}><History className="w-4 h-4" /></Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => onDelete(r.id)}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);


export default HRPayroll;
